import React, { useState } from "react";
import {
  X,
  FileBadge,
  CheckCircle,
  Loader,
  PauseCircle,
  UserCheck,
  RotateCcw,
  CircleSlash,
} from "lucide-react";
import frameBg from "../../../Assets/FrameBGDashboard.png";
import { HoldTask } from "../../../Services/TaskService";
import NotificationAlert from "../../Alerts/NotificationAlert";
 import { useNavigate } from "react-router-dom";

const HoldTaskModal = ({ show, onClose, taskData, onHold }) => {
   const navigate = useNavigate();
  const [remark, setRemark] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!show || !taskData) return null;

  // API CALL
  const handleHold = async () => {
    if (!remark.trim()) {
      setAlert({
        type: "error",
        message: "Please enter a remark before completing the task.",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        taskNo: taskData.taskNo,
        remark: remark,
      };

      const response = await HoldTask(payload);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({
          type: "success",
          message: data.message,
        });

        onHold();
        setRemark("");

        setTimeout(() => {
          onClose();
          setAlert(null);
        }, 3000);
      } else {
        setAlert({
          type: "error",
          message: data.message || "Failed to hold task",
        });
      }
    } catch (error) {
       setAlert({
        type: "error",
        message: error?.response?.data?.message || "Failed to hold task."
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    const d = new Date(date);
    return `${d.getDate()}-${d.toLocaleString("en-US", {
      month: "short",
    })}-${d.getFullYear()} ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "Assigned":
        return {
          icon: <UserCheck size={14} className="text-indigo-700" />,
          text: "Assigned",
          style: "bg-indigo-100 text-indigo-700",
        };

      case "In Progress":
        return {
          icon: <Loader size={14} className="text-blue-700 animate-spin" />,
          text: "In Progress",
          style: "bg-blue-100 text-blue-700",
        };

      case "Completed":
        return {
          icon: <CheckCircle size={14} className="text-green-700" />,
          text: "Completed",
          style: "bg-green-100 text-green-700",
        };

      case "Re-open":
        return {
          icon: <RotateCcw size={14} className="text-purple-700" />,
          text: "Re-open",
          style: "bg-purple-100 text-purple-700",
        };

      case "Closed":
        return {
          icon: <CircleSlash size={14} className="text-gray-700" />,
          text: "Closed",
          style: "bg-gray-200 text-gray-700",
        };

      case "Hold":
        return {
          icon: <PauseCircle size={14} className="text-yellow-700" />,
          text: "On Hold",
          style: "bg-yellow-100 text-yellow-700",
        };

      default:
        return {
          icon: <FileBadge size={14} className="text-gray-600" />,
          text: status,
          style: "bg-gray-100 text-gray-600",
        };
    }
  };
  const statusInfo = getStatusInfo(taskData.status);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-white  w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between text-white shadow-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold tracking-wide">Hold Task</h2>

              <span className="mt-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium w-fit">
                #{taskData.taskNo}
              </span>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition"
            >
              <X size={26} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Task Details */}
            <div className="bg-white border shadow-sm rounded-xl p-5 space-y-4 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileBadge size={16} className="text-blue-600" />
                  Task Details
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.style}`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 text-[0.93rem] text-gray-700">
                <p>
                  <span className="font-semibold">Description:</span>{" "}
                  {taskData.description}
                </p>

                <p>
                  <span className="font-semibold">Assigned By:</span>{" "}
                  {taskData.assignedByEmail}
                </p>

                <p>
                  <span className="font-semibold">Project:</span>{" "}
                  {taskData.projectName}
                </p>

                <p>
                  <span className="font-semibold">Expected Complete Date:</span>{" "}
                  {formatDateTime(taskData.estimatedComplete)}
                </p>
                
                <p>
                  <span className="font-semibold">Created At:</span>{" "}
                  {formatDateTime(taskData.createdAt)}
                </p>

                <p>
                  <span className="font-semibold">Severity:</span>{" "}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold w-fit
                    ${
                      taskData.severity === "High"
                        ? "bg-red-100 text-red-700"
                        : taskData.severity === "Moderate"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {taskData.severity}
                  </span>
                </p>
              </div>
            </div>

            {/* Remarks */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={4}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add remark..."
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none 
                       focus:ring-2 focus:ring-blue-400 shadow-sm"
              ></textarea>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 items-center shadow-inner">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 shadow-sm transition"
            >
              Cancel
            </button>

            <button
              onClick={handleHold}
              disabled={loading}
              className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition"
            >
              {loading ? "Holding..." : "Hold Task"}
            </button>
          </div>
        </div>

        {alert && (
          <NotificationAlert
            type={alert.type}
            message={alert.message}
            show={true}
            duration={4000}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </>
  );
};

export default HoldTaskModal;
